import { useRef, useState, useEffect, Component, type TextareaHTMLAttributes, type ReactNode } from "react";
import { Highlight, themes, type Language } from "prism-react-renderer";
import { useTheme } from "next-themes";
import { cn } from "@/lib/utils";

// Map language values to Prism language names
const LANGUAGE_MAP: Record<string, Language | undefined> = {
  text: undefined,
  javascript: "javascript",
  typescript: "typescript",
  python: "python",
  go: "go",
  rust: "rust",
  cpp: "cpp",
  c: "c",
  swift: "swift",
  kotlin: "kotlin",
  html: "markup",
  css: "css",
  sql: "sql",
  json: "json",
  yaml: "yaml",
  jsx: "jsx",
  tsx: "tsx",
  markdown: "markdown",
  graphql: "graphql",
  objectivec: "objectivec",
};

// Error boundary for Highlight component
interface ErrorBoundaryProps {
  children: ReactNode;
  fallback: ReactNode;
  resetKey?: string;
}

interface ErrorBoundaryState {
  hasError: boolean;
}

class HighlightErrorBoundary extends Component<ErrorBoundaryProps, ErrorBoundaryState> {
  constructor(props: ErrorBoundaryProps) {
    super(props);
    this.state = { hasError: false };
  }

  static getDerivedStateFromError(): ErrorBoundaryState {
    return { hasError: true };
  }

  componentDidUpdate(prevProps: ErrorBoundaryProps) {
    // Reset error state when key changes
    if (prevProps.resetKey !== this.props.resetKey && this.state.hasError) {
      this.setState({ hasError: false });
    }
  }

  componentDidCatch(error: Error) {
    console.warn("Syntax highlighting failed:", error.message);
  }

  render() {
    if (this.state.hasError) {
      return this.props.fallback;
    }
    return this.props.children;
  }
}

interface CodeEditorProps
  extends Omit<TextareaHTMLAttributes<HTMLTextAreaElement>, "onChange"> {
  value: string;
  onChange: (value: string) => void;
  language?: string;
  onKeyDown?: (e: React.KeyboardEvent<HTMLTextAreaElement>) => void;
}

export function CodeEditor({
  value,
  onChange,
  language = "text",
  className,
  onKeyDown,
  ...props
}: CodeEditorProps) {
  const { theme } = useTheme();
  const textareaRef = useRef<HTMLTextAreaElement>(null);
  const preRef = useRef<HTMLPreElement>(null);

  // Track language changes to defer rendering
  const [stableLanguage, setStableLanguage] = useState(language);
  const [isTransitioning, setIsTransitioning] = useState(false);

  useEffect(() => {
    if (language !== stableLanguage) {
      setIsTransitioning(true);
      // Defer the language change to avoid race conditions
      const timer = setTimeout(() => {
        setStableLanguage(language);
        setIsTransitioning(false);
      }, 0);
      return () => clearTimeout(timer);
    }
  }, [language, stableLanguage]);

  const isDark =
    theme === "dark" ||
    (theme === "system" &&
      typeof window !== "undefined" &&
      window.matchMedia("(prefers-color-scheme: dark)").matches);

  const prismLanguage = LANGUAGE_MAP[stableLanguage];

  // Sync scroll between textarea and pre
  const handleScroll = () => {
    if (textareaRef.current && preRef.current) {
      preRef.current.scrollTop = textareaRef.current.scrollTop;
      preRef.current.scrollLeft = textareaRef.current.scrollLeft;
    }
  };


  // Ensure value always ends with newline for proper rendering
  const displayValue = value || " ";

  // Plain text fallback component
  const PlainTextPre = (
    <pre
      ref={preRef}
      className="p-3 overflow-hidden pointer-events-none whitespace-pre-wrap break-words text-[hsl(var(--foreground))]"
      style={{
        margin: 0,
        fontFamily: "inherit",
        fontSize: "inherit",
        lineHeight: "1.5",
      }}
      aria-hidden="true"
    >
      {displayValue}
    </pre>
  );

  // Don't render Highlight during language transition
  const shouldHighlight = prismLanguage && value && !isTransitioning;

  return (
    <div className={cn("relative font-mono text-sm", className)}>
      {/* Syntax highlighted background */}
      {shouldHighlight ? (
        <HighlightErrorBoundary fallback={PlainTextPre} resetKey={stableLanguage}>
          <Highlight
            theme={isDark ? themes.nightOwl : themes.github}
            code={displayValue}
            language={prismLanguage}
          >
            {({ style, tokens, getLineProps, getTokenProps }) => (
              <pre
                ref={preRef}
                className="p-3 overflow-hidden pointer-events-none whitespace-pre-wrap break-words"
                style={{
                  ...style,
                  background: "transparent",
                  margin: 0,
                  fontFamily: "inherit",
                  fontSize: "inherit",
                  lineHeight: "1.5",
                }}
                aria-hidden="true"
              >
                {tokens.map((line, i) => (
                  <div key={i} {...getLineProps({ line })}>
                    {line.map((token, key) => (
                      <span key={key} {...getTokenProps({ token })} />
                    ))}
                  </div>
                ))}
              </pre>
            )}
          </Highlight>
        </HighlightErrorBoundary>
      ) : (
        PlainTextPre
      )}

      {/* Transparent textarea for input - positioned absolutely on top of pre */}
      <textarea
        ref={textareaRef}
        value={value}
        onChange={(e) => onChange(e.target.value)}
        onScroll={handleScroll}
        onKeyDown={onKeyDown}
        className={cn(
          "absolute inset-0 w-full h-full p-3 bg-transparent resize-none outline-none overflow-hidden",
          "caret-[hsl(var(--foreground))]",
          // Make text transparent so highlighting shows through
          "text-transparent",
          // Selection styling
          "selection:bg-[hsl(var(--primary))]/30 selection:text-transparent"
        )}
        style={{
          fontFamily: "inherit",
          fontSize: "inherit",
          lineHeight: "1.5",
          WebkitTextFillColor: "transparent",
        }}
        spellCheck={false}
        {...props}
      />
    </div>
  );
}
